import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { MUIDataTableColumn, MUIDataTableMeta, MUIDataTableOptions } from 'mui-datatables';
import cn from 'classnames';

import { Button, Table, View, IconClickable } from 'src/components/common';
import './styles.scss';
import { IRootState } from 'src/redux/rootReducer';
import TasksModal from './TaskModal';
import {
  getTasksAsync,
  setTaskModalType,
  setSelectedTask,
  setRefreshTable,
  deleteTaskAsync,
} from 'src/redux/task/actions';
import { GetTasksParams, TaskModalType, TaskModalTypes } from 'src/redux/task/types';
import { INITIAL_TASK } from 'src/redux/task/reducer';
import { useComponentWillUnmount } from 'src/hooks';
import { isEmpty } from 'src/validations';
import modalActions from 'src/redux/modal/actions';
import { MODAL_TYPES } from 'src/appConfig/modal';

const TasksContainer: React.FC<Props> = ({
  loading,
  tasks,
  totalTasks,
  refreshTable,
  onGetTasks,
  onShowConfirmModal,
  onCloseConfirmModal,
  onDeleteTask,
  onSetOpenModal,
  onSetSelectedTask,
  onSetRefreshTable,
}) => {
  const [refresh, doRefresh] = useState<number>(0);
  useComponentWillUnmount(() => onSetOpenModal(null as TaskModalType));

  useEffect(() => {
    if (refreshTable) {
      doRefreshTable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTable]);

  const tableColumns: MUIDataTableColumn[] = [
    { name: 'name', label: 'Tasks', options: { filter: false, sort: true } },
    {
      name: '',
      label: 'Action',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_value: any, meta: MUIDataTableMeta) => (
          <>
            <IconClickable
              className={cn('text-is-18', 'ml-2', 'ctn-task__action')}
              name="ic_edit"
              onClick={() => handleEditRow(meta)}
            />
            <IconClickable
              className={cn('text-is-18', 'ml-2', 'ctn-task__action')}
              name="ic_trash"
              onClick={() => onDeleteRow(meta)}
            />
          </>
        ),
      },
    },
  ];

  const filterTasksParams = (params: GetTasksParams) => {
    let res = params;
    Object.keys(res).forEach(key => {
      if (isEmpty(res[key])) delete res[key];
      if (key === 'status') {
        res['status'] = res.status[0].toLowerCase();
      }
    });
    return res;
  };
  const handleGetEmployees = (params: GetTasksParams) => {
    onGetTasks(filterTasksParams(params));
  };

  const handleEditRow = (meta: { rowIndex: number }) => {
    const index = meta.rowIndex;
    onSetSelectedTask(tasks[index]);
    onSetOpenModal(TaskModalTypes.EDIT as TaskModalType);
  };

  const onDeleteRow = meta => {
    const taskName = meta.rowData[0] || '';

    onShowConfirmModal({
      type: MODAL_TYPES.yesNoModal,
      data: {
        title: 'Delete Task',
        message: `Are you sure that you want to delete this task? \n "${taskName}"`,
        onOk: () => handleDeleteRow(meta),
        onCancel: onCloseConfirmModal,
      },
    });
  };

  const handleDeleteRow = meta => {
    const task = tasks[meta.rowIndex];

    onCloseConfirmModal();
    onDeleteTask({ id: task.id || 0 });
  };

  const handleCloseModal = () => {
    onSetSelectedTask(INITIAL_TASK);
    onSetOpenModal(false as TaskModalType);
  };

  const doRefreshTable = () => {
    doRefresh(prev => prev + 1);
    onSetRefreshTable(false);
  };

  const tableOptions: MUIDataTableOptions = {
    count: totalTasks,
    searchPlaceholder: 'Search by task',
  };

  return (
    <View className="ctn-task mt-32">
      <View className="ctn-task__header mb-32">
        <View>
          <h3>Manage Tasks</h3>
        </View>
        <View>
          <Button onClick={() => onSetOpenModal(TaskModalTypes.ADD as TaskModalType)}>ADD NEW TASK</Button>
        </View>
      </View>

      <Table
        title=""
        filter={false}
        data={tasks}
        tableOptions={tableOptions}
        isLoading={loading}
        refresh={refresh}
        defaultSortOrder={{ name: 'name', direction: 'desc' }}
        columns={tableColumns}
        onAction={handleGetEmployees}
      />
      <TasksModal onClose={handleCloseModal} />
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const mapStateToProps = (state: IRootState) => ({
  loading: state.task.isLoading,
  tasks: state.task.tasks,
  totalTasks: state.task.totalTasks,
  refreshTable: state.task.isRefreshTable,
  status: state.content.status,
});

const mapDispatchToProps = {
  onGetTasks: getTasksAsync.request,
  onDeleteTask: deleteTaskAsync.request,
  onShowConfirmModal: modalActions.showModal,
  onCloseConfirmModal: modalActions.closeModal,
  onSetOpenModal: setTaskModalType,
  onSetSelectedTask: setSelectedTask,
  onSetRefreshTable: setRefreshTable,
};

export default connect(mapStateToProps, mapDispatchToProps)(TasksContainer);
